import { useEffect, useRef, useState } from 'react';
import { ApiError, joinLobby, listPublicLobbies, type LobbyTicket, type PublicLobbyRow, type GameTile } from '../../lib/api';
import { Character3DThumbnail } from '../characters/Character3DThumbnail';
import { useEscapeClose } from '../../lib/useEscapeClose';

interface Props {
  onJoined: (ticket: LobbyTicket) => void;
  onClose: () => void;
  games: GameTile[];
}

const POLL_MS_BASE = 5000;
const POLL_MS_MAX = 30_000;
const QUIET_TO_BACKOFF = 5;

export function PublicBrowser({ onJoined, onClose, games }: Props) {
  const [rows, setRows] = useState<PublicLobbyRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyCode, setBusyCode] = useState<string | null>(null);
  const aliveRef = useRef(true);
  useEscapeClose(true, onClose);

  // R2#16: pause polling when tab is hidden, back off when nothing
  // changes, reset on visibility return or successful join attempts.
  // Quiet streak counts consecutive polls that returned the same row
  // count + same first roomCode. After QUIET_TO_BACKOFF, the interval
  // doubles each cycle up to POLL_MS_MAX. Visibility return clears
  // both the streak and the interval.
  const intervalRef = useRef<number>(POLL_MS_BASE);
  const quietRef = useRef<number>(0);
  const lastSigRef = useRef<string | null>(null);
  const timerRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    aliveRef.current = true;

    function isVisible() {
      try { return document.visibilityState === 'visible'; } catch { return true; }
    }

    function rowsSig(items: PublicLobbyRow[]): string {
      return `${items.length}|${items[0]?.roomCode ?? ''}`;
    }

    function scheduleNext() {
      if (!aliveRef.current) return;
      if (timerRef.current !== undefined) {
        window.clearTimeout(timerRef.current);
        timerRef.current = undefined;
      }
      // Skip scheduling while hidden — the visibilitychange handler
      // will fire an immediate tick when the tab returns.
      if (!isVisible()) return;
      timerRef.current = window.setTimeout(() => void tick(), intervalRef.current);
    }

    async function tick() {
      if (!aliveRef.current) return;
      // Defensive — if visibility flipped between schedule and fire.
      if (!isVisible()) { scheduleNext(); return; }
      try {
        const res = await listPublicLobbies();
        if (!aliveRef.current) return;
        setRows(res.items);
        setError(null);
        const sig = rowsSig(res.items);
        if (sig === lastSigRef.current) {
          quietRef.current += 1;
          if (quietRef.current >= QUIET_TO_BACKOFF) {
            intervalRef.current = Math.min(POLL_MS_MAX, intervalRef.current * 2);
          }
        } else {
          quietRef.current = 0;
          intervalRef.current = POLL_MS_BASE;
          lastSigRef.current = sig;
        }
      } catch {
        if (aliveRef.current) setError('Could not load tables.');
      } finally {
        scheduleNext();
      }
    }

    function onVisibility() {
      if (isVisible()) {
        // Reset back to the base cadence and fire immediately.
        intervalRef.current = POLL_MS_BASE;
        quietRef.current = 0;
        if (timerRef.current !== undefined) {
          window.clearTimeout(timerRef.current);
          timerRef.current = undefined;
        }
        void tick();
      } else {
        if (timerRef.current !== undefined) {
          window.clearTimeout(timerRef.current);
          timerRef.current = undefined;
        }
      }
    }

    document.addEventListener('visibilitychange', onVisibility);
    void tick();
    return () => {
      aliveRef.current = false;
      document.removeEventListener('visibilitychange', onVisibility);
      if (timerRef.current !== undefined) window.clearTimeout(timerRef.current);
    };
  }, []);

  function gameLabel(id: string): string {
    return games.find((g) => g.id === id)?.name ?? id;
  }

  async function tryJoin(row: PublicLobbyRow) {
    setBusyCode(row.roomCode);
    setError(null);
    // R2#16: any join attempt likely changes lobby population, so reset
    // the back-off cadence so the next poll catches the change quickly.
    intervalRef.current = POLL_MS_BASE;
    quietRef.current = 0;
    try {
      const t = await joinLobby(row.roomCode);
      onJoined(t);
    } catch (e) {
      setBusyCode(null);
      const code = e instanceof ApiError ? e.code : '';
      if (code === 'lobby-full') setError('That table just filled up.');
      else if (code === 'lobby-not-found' || code === 'lobby-closed') setError('That table is gone now.');
      else setError('Could not join — try another.');
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Browse public tables"
      className="public-browser-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="public-browser-modal">
        <header className="public-browser-head">
          <h2 className="display">Open tables</h2>
          <button type="button" className="btn ghost" onClick={onClose}>Close</button>
        </header>
        <p className="muted public-browser-sub">
          Public rooms looking for players. Tap one to pull up a chair.
        </p>

        {error && <div role="alert" className="gate-rejection public-browser-error">{error}</div>}

        <div className="public-browser-list">
          {rows === null && (
            <div className="muted public-browser-empty">Looking…</div>
          )}
          {rows !== null && rows.length === 0 && (
            <div className="muted public-browser-empty">
              Nobody's hosting right now. Open one yourself?
            </div>
          )}
          {rows?.map((row) => (
            <button
              key={row.roomCode}
              type="button"
              onClick={() => void tryJoin(row)}
              disabled={busyCode !== null}
              className={`public-browser-row${busyCode !== null && busyCode !== row.roomCode ? ' is-dimmed' : ''}${busyCode === row.roomCode ? ' is-joining' : ''}`}
            >
              <Character3DThumbnail icon={row.hostIcon} size={44} alt={row.hostName} />
              <div className="public-browser-row-text">
                <div className="public-browser-row-title">
                  {row.hostName}'s table
                </div>
                <div className="muted public-browser-row-meta">
                  {gameLabel(row.gameId)} · {row.liveSeats}/{row.maxPlayers} seats · code {row.roomCode}
                </div>
              </div>
              <span className="public-browser-join">
                {busyCode === row.roomCode ? 'joining…' : 'join →'}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
